import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  fetchActionHistory,
  fetchManagementQueue,
  fetchManagementSummary,
  ManagementQueueItem,
  submitManagementTransition,
} from "@/api/managementApi";
import {
  AvailableAction,
  fetchAvailableActions,
  formatWorkflowActionLabel,
} from "@/api/workflowApi";
import { formatWorkflowStatusLabel } from "@/utils/semanticLabels";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface SummaryStats {
  totalReviewed: number;
  totalApproved: number;
  totalRejected: number;
  pendingEscalations: number;
}

const isSummaryStats = (value: unknown): value is SummaryStats => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.totalReviewed === "number" &&
    typeof candidate.totalApproved === "number" &&
    typeof candidate.totalRejected === "number" &&
    typeof candidate.pendingEscalations === "number"
  );
};

interface HistoryEntry {
  actor: string | number;
  role: string;
  action: string;
  remarks?: string | null;
  timestamp: string;
  from_state?: string | null;
  to_state?: string | null;
}

const emptySummary: SummaryStats = {
  totalReviewed: 0,
  totalApproved: 0,
  totalRejected: 0,
  pendingEscalations: 0,
};

const StatCard = ({ label, value }: { label: string; value: number }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-2xl font-semibold">{value}</p>
    </CardContent>
  </Card>
);

export default function ManagementDashboard() {
  const [summary, setSummary] = useState<SummaryStats>(emptySummary);
  const [queue, setQueue] = useState<ManagementQueueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submissionId, setSubmissionId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [actions, setActions] = useState<AvailableAction[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [transitioningAction, setTransitioningAction] = useState<string | null>(null);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const [data, queueData] = await Promise.all([
        fetchManagementSummary(),
        fetchManagementQueue(),
      ]);
      setSummary(isSummaryStats(data) ? data : emptySummary);
      setQueue(queueData.items);
    } catch {
      toast.error("Failed to load management summary");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    const id = Number(submissionId);
    if (!Number.isInteger(id) || id <= 0) {
      toast.error("Enter a valid submission ID");
      return;
    }

    try {
      const data = await fetchActionHistory(id);
      setHistory(data.history ?? []);
    } catch {
      toast.error("Failed to load action history");
    }
  };

  const loadActions = async () => {
    const id = Number(submissionId);
    if (!Number.isInteger(id) || id <= 0) {
      toast.error("Enter a valid submission ID");
      return;
    }

    try {
      setActionLoading(true);
      const data = await fetchAvailableActions(id);
      setActions(data.actions ?? []);
    } catch (err: any) {
      setActions([]);
      toast.error(err?.response?.data?.detail ?? "Failed to load available actions");
    } finally {
      setActionLoading(false);
    }
  };

  const submitWorkflowAction = async (actionCode: string) => {
    const id = Number(submissionId);
    if (!Number.isInteger(id) || id <= 0) {
      toast.error("Enter a valid submission ID");
      return;
    }

    if (!actions.some((action) => action.action_code === actionCode)) {
      toast.error("Application status changed. Reload available decisions.");
      return;
    }

    try {
      setTransitioningAction(actionCode);
      await submitManagementTransition(id, {
        action_code: actionCode,
        remarks: remarks.trim() || undefined,
      });
      toast.success("Decision recorded");
      setRemarks("");
      await loadActions();
      await loadSummary();
      await loadHistory();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Decision could not be saved");
    } finally {
      setTransitioningAction(null);
    }
  };

  const selectQueueItem = (item: ManagementQueueItem) => {
    setSubmissionId(String(item.submission_id));
    setActions([]);
    setHistory([]);
  };

  useEffect(() => {
    loadSummary();
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Management Approvals</h1>
        <Button variant="outline" onClick={loadSummary} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 text-sm text-blue-950">
          <div className="font-semibold">What should I do next?</div>
          <p className="mt-1">
            {summary.pendingEscalations > 0
              ? "Review applications waiting for management approval."
              : "No application is waiting for management approval right now."}
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Management Reviews Completed" value={summary.totalReviewed} />
        <StatCard label="Management Review Approved" value={summary.totalApproved} />
        <StatCard label="Management Review Rejected" value={summary.totalRejected} />
        <StatCard label="Awaiting management action" value={summary.pendingEscalations} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Canonical Management Queue</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {queue.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No submission is currently assigned to Management for this tenant.
            </p>
          ) : (
            queue.map((item) => (
              <button
                key={item.submission_id}
                type="button"
                className="w-full rounded-md border bg-white p-4 text-left hover:border-blue-300"
                onClick={() => selectQueueItem(item)}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">Submission #{item.submission_id}</div>
                    <div className="text-sm text-muted-foreground">
                      {item.program_code || "No program"} - {item.status.replaceAll("_", " ")}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {item.executable_action_count} backend action
                    {item.executable_action_count === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {item.review_state.replaceAll("_", " ")} -{" "}
                  {item.blocking_reason.replaceAll("_", " ")}
                </div>
              </button>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Review Application Decision</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[220px_1fr]">
            <Input
              inputMode="numeric"
              placeholder="Submission ID"
              value={submissionId}
              onChange={(event) => {
                setSubmissionId(event.target.value);
                setActions([]);
                setHistory([]);
              }}
            />
            <Textarea
              placeholder="Remarks"
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={loadActions} disabled={actionLoading}>
              {actionLoading ? "Loading..." : "Load Available Decisions"}
            </Button>
            <Button variant="secondary" onClick={loadHistory}>
              Load Decision History
            </Button>
          </div>

          {actions.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {actions.map((action) => (
                <Button
                  key={action.action_code}
                  variant="outline"
                  disabled={!!transitioningAction}
                  onClick={() => submitWorkflowAction(action.action_code)}
                >
                  {transitioningAction === action.action_code
                    ? "Saving decision..."
                    : `${formatWorkflowActionLabel(action.action_code)} → ${formatWorkflowStatusLabel(action.to_status)}`}
                </Button>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Decision History</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No decision history loaded.</p>
          ) : (
            <div className="space-y-3">
              {history.map((entry, index) => (
                <div key={`${entry.timestamp}-${index}`} className="border-b pb-3 text-sm">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-medium">{entry.action}</span>
                    <span className="text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-muted-foreground">
                    Actor {entry.actor} ({entry.role})
                    {entry.from_state || entry.to_state
                      ? `: ${entry.from_state ?? "-"} -> ${entry.to_state ?? "-"}`
                      : ""}
                  </div>
                  {entry.remarks ? <div>{entry.remarks}</div> : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
